"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  alpha,
} from "@mui/material"
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Cameraswitch as CameraswitchIcon,
  Close as CloseIcon,
} from "@mui/icons-material"

const VentaCrearMobileLayout = ({
  // Search and add
  selectedItem,
  handleItemChange,
  setSearchText,
  itemTresLetras,
  generateItemLabel,
  // Item table
  itemList,
  cantidadInputs,
  setCantidadInputs,
  handleUpdateItem,
  precioInputs,
  setPrecioInputs,
  handleUpdateItemPorPrecio,
  precioEnVentaEditable,
  handleDeleteItem,
  // Footer
  precioTotal,
  onOpenConfig,
  onGoToPayment,
  canGoToPayment,
  isDrawerOpen,
  // Scanner
  enableScanner,
}) => {
  const [scannerOpen, setScannerOpen] = useState(false)
  const [scannerError, setScannerError] = useState("")
  const [videoDevices, setVideoDevices] = useState([])
  const [selectedDeviceId, setSelectedDeviceId] = useState("")
  const videoRef = useRef(null)
  const scannerReaderRef = useRef(null)

  const stopScanner = useCallback(() => {
    if (scannerReaderRef.current) {
      try {
        scannerReaderRef.current.reset()
      } catch (error) {
        console.warn("Error cerrando scanner:", error)
      }
      scannerReaderRef.current = null
    }
  }, [])

  const closeScanner = useCallback(() => {
    setScannerOpen(false)
    setScannerError("")
    stopScanner()
  }, [stopScanner])

  const initializeScanner = useCallback(async () => {
    try {
      setScannerError("")

      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        setScannerError("Para usar camara se requiere HTTPS o localhost.")
        return
      }

      const { BrowserMultiFormatReader } = await import("@zxing/browser")
      const { BarcodeFormat, DecodeHintType } = await import("@zxing/library")

      const hints = new Map()
      hints.set(DecodeHintType.TRY_HARDER, true)
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.QR_CODE,
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.CODE_128,
        BarcodeFormat.CODE_39,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.ITF,
        BarcodeFormat.DATA_MATRIX,
        BarcodeFormat.PDF_417,
      ])

      const reader = new BrowserMultiFormatReader(hints, 250)
      scannerReaderRef.current = reader

      const devices = await BrowserMultiFormatReader.listVideoInputDevices()
      setVideoDevices(devices)

      let deviceId = selectedDeviceId
      if (!deviceId && devices.length > 0) {
        const preferred = devices.find((device) =>
          String(device.label || "").toLowerCase().match(/back|rear|environment|trasera/)
        )
        deviceId = preferred?.deviceId || devices[0].deviceId
        setSelectedDeviceId(deviceId)
      }

      const videoElement = videoRef.current
      if (!videoElement) {
        setScannerError("No se encontro el visor de camara.")
        return
      }

      await reader.decodeFromVideoDevice(deviceId || undefined, videoElement, (result, error) => {
        if (result) {
          const codigo = result.getText()
          console.log("Codigo escaneado venta mobile:", codigo)
          setSearchText(codigo)
          closeScanner()
        } else if (error && !String(error?.message || "").includes("No MultiFormat Readers")) {
          console.warn("Scanner warning:", error)
        }
      })
    } catch (error) {
      console.error("Error iniciando scanner:", error)
      setScannerError("No se pudo abrir la camara. Revise permisos del navegador.")
    }
  }, [closeScanner, selectedDeviceId, setSearchText])

  useEffect(() => {
    if (!scannerOpen || !enableScanner) return undefined
    initializeScanner()
    return () => {
      stopScanner()
    }
  }, [scannerOpen, enableScanner, initializeScanner, stopScanner])

  useEffect(() => {
    return () => {
      stopScanner()
    }
  }, [stopScanner])

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        backgroundColor: "#f5f5f5",
        pb: 9,
        overflow: "hidden",
      }}
    >
      <Card elevation={2} sx={{ m: 1, mb: 0.75, borderRadius: 1.5 }}>
        <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>
              venta
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={onOpenConfig}
              startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
              sx={{ minHeight: 28, fontSize: 11, px: 1 }}
            >
              Configurar
            </Button>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <Autocomplete
              size="small"
              disablePortal
              options={(itemTresLetras || []).map((item) => ({
                label: generateItemLabel(item),
                descripcion: item.descripcion,
                codigo: item.codigo,
                id: item.id,
                codigoScanner: item.codigoScanner,
                usaGestionLotes: item.usaGestionLotes || false,
              }))}
              value={selectedItem}
              onChange={handleItemChange}
              onInputChange={(event, newInputValue) => setSearchText(newInputValue)}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Producto"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon color="action" sx={{ mr: 0.5, fontSize: 18 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{
                "& .MuiInputBase-root": {
                  minHeight: 34,
                },
              }}
            />

            {enableScanner && (
              <Tooltip title="Escanear">
                <IconButton
                  size="small"
                  onClick={() => setScannerOpen(true)}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    width: 32,
                    height: 32,
                  }}
                >
                  <QrCodeScannerIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card
        elevation={2}
        sx={{
          m: 1,
          mt: 0,
          borderRadius: 1.5,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#efefef", py: 0.6, px: 0.7, fontSize: 11, fontWeight: 700 }}>
                  Producto
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: "#efefef", py: 0.6, px: 0.5, fontSize: 11, fontWeight: 700, width: 56 }}
                >
                  Cant.
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: "#efefef", py: 0.6, px: 0.5, fontSize: 11, fontWeight: 700, width: 84 }}
                >
                  Precio
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ backgroundColor: "#efefef", py: 0.6, px: 0.5, fontSize: 11, fontWeight: 700, width: 86 }}
                >
                  Total
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ backgroundColor: "#efefef", py: 0.6, px: 0.25, fontSize: 11, fontWeight: 700, width: 34 }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {itemList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 2, fontSize: 12, color: "text.secondary" }}>
                    Sin items
                  </TableCell>
                </TableRow>
              ) : (
                itemList.map((item, index) => {
                  const rowKey = `${item.idItem}-${item.idUbicacion}`
                  return (
                    <TableRow key={rowKey} sx={{ "&:hover": { backgroundColor: alpha("#5b5b5f", 0.04) } }}>
                      <TableCell sx={{ py: 0.5, px: 0.7 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2 }}>
                          {item.codigo} - {item.descripcion}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" sx={{ py: 0.5, px: 0.5 }}>
                        <TextField
                          size="small"
                          value={cantidadInputs[rowKey] ?? ""}
                          onChange={(event) => {
                            setCantidadInputs((prev) => ({
                              ...prev,
                              [rowKey]: event.target.value,
                            }))
                          }}
                          onBlur={() => {
                            const raw = cantidadInputs[rowKey] ?? ""
                            const parsed = Number.parseFloat(raw)
                            const finalValue = Number.isNaN(parsed) || parsed <= 0 ? item.cantidad : parsed
                            handleUpdateItem(index, item.descripcion, finalValue, item.descuento)
                            setCantidadInputs((prev) => ({
                              ...prev,
                              [rowKey]: finalValue.toString(),
                            }))
                          }}
                          type="number"
                          inputProps={{
                            step: "0.001",
                            min: "0.001",
                            style: { fontSize: 11, textAlign: "right", padding: "4px 6px" },
                          }}
                          sx={{
                            width: 52,
                            "& .MuiInputBase-root": { height: 24 },
                          }}
                        />
                      </TableCell>

                      <TableCell align="right" sx={{ py: 0.5, px: 0.5 }}>
                        {precioEnVentaEditable ? (
                          <TextField
                            size="small"
                            value={precioInputs[rowKey] ?? ""}
                            onChange={(event) => {
                              setPrecioInputs((prev) => ({
                                ...prev,
                                [rowKey]: event.target.value,
                              }))
                            }}
                            onBlur={() => {
                              const raw = precioInputs[rowKey] ?? ""
                              const parsed = Number.parseFloat(String(raw).replace(",", "."))
                              const finalValue = Number.isNaN(parsed) || parsed <= 0 ? item.precio : parsed
                              handleUpdateItemPorPrecio(index, finalValue)
                              setPrecioInputs((prev) => ({
                                ...prev,
                                [rowKey]: finalValue.toString(),
                              }))
                            }}
                            type="number"
                            inputProps={{
                              step: "0.01",
                              min: "0.01",
                              style: { fontSize: 11, textAlign: "right", padding: "4px 6px" },
                            }}
                            sx={{
                              width: 80,
                              "& .MuiInputBase-root": { height: 24 },
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                            ${Number(item.precio || 0).toFixed(2)}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="right" sx={{ py: 0.5, px: 0.5 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "success.main" }}>
                          ${Number(item.precio * item.cantidad * (1 - (item.porcentajeDescuento || 0) / 100)).toFixed(2)}
                        </Typography>
                      </TableCell>

                      <TableCell align="center" sx={{ py: 0.5, px: 0.25 }}>
                        <IconButton color="error" size="small" onClick={() => handleDeleteItem(index)} sx={{ p: 0.25 }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {!isDrawerOpen && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            px: 1,
            pt: 0.5,
            pb: 0.75,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
            zIndex: 1300,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>
              Items:{" "}
              <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
                {itemList.length}
              </Box>
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>
              Total:{" "}
              <Box component="span" sx={{ color: "success.main", fontWeight: 700 }}>
                ${Number(precioTotal || 0).toFixed(2)}
              </Box>
            </Typography>
          </Box>
          <Button fullWidth variant="contained" size="small" type="button" onClick={onGoToPayment}>
            {canGoToPayment ? "Pagar" : "Ir a pago"}
          </Button>
        </Box>
      )}

      <Modal open={scannerOpen} onClose={closeScanner}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "92vw",
            maxWidth: 420,
            bgcolor: "background.paper",
            borderRadius: 1.5,
            p: 1,
            boxShadow: 24,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13 }}>Scanner</Typography>
            <IconButton size="small" onClick={closeScanner}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {scannerError && <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>{scannerError}</Alert>}

          <Box
            sx={{
              width: "100%",
              height: 250,
              bgcolor: "#111",
              borderRadius: 1,
              overflow: "hidden",
              mb: 1,
            }}
          >
            <video ref={videoRef} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </Box>

          {videoDevices.length > 0 && (
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="scanner-camera-select-label">Camara</InputLabel>
                <Select
                  labelId="scanner-camera-select-label"
                  value={selectedDeviceId}
                  label="Camara"
                  onChange={(event) => setSelectedDeviceId(event.target.value)}
                >
                  {videoDevices.map((device, index) => (
                    <MenuItem key={device.deviceId} value={device.deviceId}>
                      {device.label || `Camara ${index + 1}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={() => {
                  if (videoDevices.length < 2) return
                  const currentIndex = videoDevices.findIndex((device) => device.deviceId === selectedDeviceId)
                  const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % videoDevices.length : 0
                  setSelectedDeviceId(videoDevices[nextIndex].deviceId)
                }}
              >
                <CameraswitchIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  )
}

export default VentaCrearMobileLayout
